import React, { useEffect, useMemo, useState } from "react";
import "./style.scss";
import ReactApexChart from "react-apexcharts";
import Tabination from "../../../../reusableComponents/Tabination";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker } from "antd";
import { getAccountMetrics } from "../../../../store/reducers/accountSlice";

import ReactECharts from "echarts-for-react";
import { formatCurrency } from "../../../../utils/helpers/string";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

function FinancialPerformance({ chartData }) {
  const items = [
    // { name: "Growth", content: <GrowthChart /> },
    { name: "Balance", content: <BalanceChart /> },
    {
      name: "Profit",
      content: <ProfitChart chartData={chartData.profit_chart} />,
    },
    {
      name: "Drawdown",
      content: <DrawdownChart chartData={chartData.drawdown_chart} />,
    },
  ];

  return (
    <div className="financialperformance-container">
      <div className="chart_heading">
        <h4>Financial Performance</h4>{" "}
        <p
          className={`account_status ${
            chartData?.status === "in_progress"
              ? "active_progress"
              : chartData?.status === "failed" ||
                chartData?.status === "expired"
              ? "active_failed"
              : "active_passed"
          }`}
        >
          {chartData?.status?.replace("_", " ")}
        </p>
      </div>
      <div className="financialperformance-subcontainer">
        <div className="financialperformance-innercontainer">
          <Tabination
            variant={2}
            activeTab={0}
            tabItems={items}
            isChart={true}
            isToolbar={true}
            balanceChart={true}
          />
        </div>
      </div>
    </div>
  );
}

export default FinancialPerformance;

export function FilterChart() {
  const { RangePicker } = DatePicker;

  const dispatch = useDispatch();
  const accountsData = useSelector(
    (state) => state.account.accountMetrics.data
  );
  const idToken = useSelector((state) => state.auth.idToken);
  const activeAccountRedux = useSelector(
    (state) => state.account.accountList.activeAccount.id
  );

  const [balance_chart, setbalance_chart] = useState([]);
  const [selection, setSelection] = useState("all");

  // Function to fetch data and dispatch the API call
  const fetchData = (startDate, endDate) => {
    dispatch(
      getAccountMetrics({
        idToken: idToken,
        loginId: activeAccountRedux,
        ...(startDate &&
          endDate && {
            start_date: startDate?.format("YYYY-MM-DD"), // Use formatted local date
            end_date: endDate?.format("YYYY-MM-DD"),
          }),
      })
    );
  };

  // Handle date range changes
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;

      const startLocal = start.startOf("day"); // Local time (start of the day)
      const endLocal = end.endOf("day"); // Local time (end of the day)

      setSelection("custom");

      fetchData(startLocal, endLocal);
    } else {
      fetchData(null, null);
    }
  };

  return (
    <div className="toolbar">
      <RangePicker
        onChange={handleDateRangeChange}
        format="YYYY-MM-DD"
        style={{ marginLeft: "1rem" }}
        disabledDate={(current) =>
          current && current > moment.utc().endOf("day")
        }
      />
    </div>
  );
}

const BalanceChart = () => {
  const { selectedBrand } = useSelector((state) => state.brand);
  const accountsData = useSelector((state) => state.account.accountMetrics.data);

  // 1. Prepare Data
  const chartData = useMemo(() => {
    return accountsData?.balance_chart?.length > 0
      ? accountsData.balance_chart
      : [{ balance: 0, equity: 0, time: Date.now() }];
  }, [accountsData]);

  // 2. Define Theme Colors
  const theme = useMemo(() => {
    const isCrypto = selectedBrand?.brand === "crypto_edge";
    return {
      balanceColor: isCrypto ? "#FCD535" : "#80E0C4",
      equityColor: isCrypto ? "#ED622B" : "#36d66b",
      tooltipBorder: isCrypto ? "#FCD535" : "#36d66b",
      tooltipLine: isCrypto ? "#ED622B" : "#36d66b",
    };
  }, [selectedBrand]);

  const { highestBalance, highestEquity } = useMemo(() => {
    return {
      highestBalance: accountsData?.program_objectives?.highest?.highest_balance ?? null,
      highestEquity: accountsData?.program_objectives?.highest?.highest_equity ?? null,
    };
  }, [accountsData]);

// 3. Chart Configuration
const option = useMemo(() => {
  const dates = chartData.map((entry) =>
    dayjs.utc(entry.time).format("DD-MMM-YY hh:mm A")
  );
  const balanceData = chartData.map((entry) => entry.balance);
  const equityData = chartData.map((entry) => entry.equity);

  return {
    tooltip: {
      trigger: "axis", // <--- CRITICAL: Ensures both Balance & Equity show
      backgroundColor: "#fff",
      textStyle: { color: "#000" },
      borderColor: theme.tooltipBorder,
      borderWidth: 2,
      padding: [10, 15],
      // confne ensures tooltip doesn't get cut off at screen edges
      confine: true, 
      axisPointer: {
        type: "line",
        axis: "x",
        snap: true, // <--- CRITICAL: Locks the tooltip to the nearest data point
        lineStyle: {
          color: theme.tooltipLine,
          width: 1,
          type: "dashed",
        },
      },
      formatter: (params) => {
        if (!Array.isArray(params) || !params.length) return "";
        
        // Use axisValue from the first param to ensure date matches the hover
        const title = params[0].axisValue;
        
        let tooltipContent = `<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px; color: #333;">${title}</div>`;

        params.forEach((item) => {
          // Safety check: ensure value exists
          if (item.value !== undefined && item.value !== null) {
            const formattedValue = formatCurrency(item.value);
            // item.marker contains the colored dot automatically generated by ECharts
            // or we can use the custom HTML you wrote:
            tooltipContent += `
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 15px; margin-bottom: 5px;">
                <div style="display: flex; align-items: center;">
                  <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${item.color}; margin-right: 8px;"></div>
                  <span style="font-weight: 500; color: #555;">${item.seriesName}</span>
                </div>
                <span style="font-weight: 700; color: #000;">${formattedValue}</span>
              </div>`;
          }
        });

        return tooltipContent;
      },
    },
    legend: {
      data: [
        { name: "Balance", icon: "circle", itemStyle: { color: theme.balanceColor } },
        { name: "Equity", icon: "circle", itemStyle: { color: theme.equityColor } },
      ],
      textStyle: { fontWeight: 700, color: "#010101" },
    },
    grid: {
      top: 60,
      left: "3%",
      right: "30px",
      bottom: "3%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: dates,
      axisLabel: {
        rotate: 0,
        color: "#010101",
        showMaxLabel: true,
        hideOverlap: true,
      },
    },
    yAxis: {
      type: "value",
      scale: true,
      axisLabel: {
        formatter: (value) => `$${value}`,
        color: "#010101",
      },
      min: (value) => Math.floor(value.min - (value.min * 0.05)),
    },
    series: [
      {
        name: "Balance",
        type: "line",
        data: balanceData,
        showSymbol: false,
        // Removed 'sampling: lttb' to ensure tooltips always find both points
        smooth: true,
        itemStyle: { color: theme.balanceColor },
        lineStyle: { width: 3, color: theme.balanceColor },
      },
      {
        name: "Equity",
        type: "line",
        data: equityData,
        showSymbol: false,
        // Removed 'sampling: lttb'
        smooth: true,
        itemStyle: { color: theme.equityColor },
        lineStyle: { width: 3, color: theme.equityColor },
      },
    ],
  };
}, [chartData, theme]);

  return (
    <>
      {highestBalance !== null && highestEquity !== null && (
        <div className="highestBalanceAndEquity">
          <p>
            Highest Balance: <span>{formatCurrency(highestBalance)}</span>
          </p>
          <p>
            Highest Equity: <span>{formatCurrency(highestEquity)}</span>
          </p>
        </div>
      )}

      <ReactECharts option={option} style={{ height: 400 }} notMerge={true} />
    </>
  );
};
function ProfitChart() {
  const accountsData = useSelector((state) => state.account.accountMetrics);
  const [date, setdate] = useState([]);
  const [profit, setprofit] = useState([]);
  useEffect(() => {
    setdate(accountsData?.data?.profit_chart?.date || []);
    setprofit(accountsData?.data?.profit_chart?.profit || []);
  }, [accountsData]);

  const positiveArray = profit?.map((value) => Math.abs(value));
  const selectedBrand = useSelector((state) => state.brand.selectedBrand);

  const formattedDates = date?.map((dateString) => {
    const date = new Date(dateString);
    const utcDate = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );

    return utcDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      timeZone: "UTC",
    });
  });

  const series = [
    {
      name: "Balance",
      type: "column",
      data: positiveArray,
    },
    {
      name: "profit",
      type: "line",
      data: positiveArray,
    },
  ];

  const options = {
    chart: {
      height: 350,
      type: "line",
      toolbar: {
        show: true,
        offsetY: -100,
        tools: {
          download: false,
          selection: false,
          zoom: false,
          zoomin: true,
          zoomout: true,
          pan: false,
          reset: false,
        },
        zoom: {
          enabled: false,
        },
      },
    },
    tooltip: {
      enabled: true,
      x: {
        formatter: formattedDates,
      },
    },
    stroke: {
      width: [0, 4],
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [1],
    },
    xaxis: {
      categories: formattedDates,
      labels: {
        style: {
          colors: "#010101",
        },
        rotate: 0,
      },
      tickAmount: 6,
      formatter: function (value, index) {
        return index % 10 === 0 ? value : "";
      },
    },
    plotOptions: {
      bar: {
        columnWidth: "30%",
      },
    },
    legend: {
      horizontalAlign: "left",
      offsetX: 40,
      fontWeight: 700,
      markers: {
        radius: 50,
      },
      itemMargin: {
        horizontal: 10,
      },
    },
    yaxis: [
      {
        labels: {
          style: {
            colors: "#010101",
          },
          formatter: (value) => `$${value?.toFixed(2) ?? ""}`,
        },
      },
    ],
    colors: ["#80E0C4", "#f25f5c"],
  };

  return <ReactApexChart options={options} series={series} height={300} />;
}

function DrawdownChart() {
  const accountsData = useSelector((state) => state.account.accountMetrics);
  const [date, setdate] = useState([]);
  const [draw_down, setdraw_down] = useState([]);
  useEffect(() => {
    const dateData = accountsData?.data?.drawdown_chart?.date || [];
    const drawDownData = accountsData?.data?.drawdown_chart?.draw_down || [];

    setdate(dateData || []);
    setdraw_down(drawDownData || []);
  }, [accountsData]);

  const negativeArray = draw_down?.map((value) => -value);

  const formattedDates = date?.map((dateString) => {
    const date = new Date(dateString);
    const utcDate = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );

    return utcDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      timeZone: "UTC",
    });
  });

  const series = [
    {
      name: "Loss",
      data: negativeArray,
    },
  ];

  const options = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        offsetY: -10,
        tools: {
          download: false,
          selection: false,
          zoom: false,
          zoomin: true,
          zoomout: true,
          pan: false,
          reset: false,
        },
        zoom: {
          enabled: false,
        },
      },
    },
    labels: formattedDates,
    xaxis: {
      labels: {
        style: {
          colors: "#010101",
        },
      },
    },
    yaxis: [
      {
        labels: {
          style: {
            colors: "#010101",
          },
          formatter: (value) => `$${Math.abs(value)?.toFixed(2) ?? ""}`,
        },
        max: 0,
      },
    ],
    fill: {
      gradient: {
        shade: "dark",
        type: "vertical",
        shadeIntensity: 0,
        opacityFrom: 1,
        opacityTo: 0,
        stops: [0, 100],
        colorStops: [],
        inverseColors: false,
      },
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: true,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "20%",
        borderRadius: 0,
        colors: {
          ranges: [
            {
              from: -Infinity,
              to: 0,
              color: "#f25f5c",
            },
          ],
        },
      },
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [1],
    },
    legend: {
      position: "bottom",
      show: true,
      horizontalAlign: "left",
      offsetX: 40,
      fontWeight: 700,
      markers: {
        radius: 50,
      },
      itemMargin: {
        horizontal: 10,
      },
    },
  };

  return (
    <ReactApexChart options={options} series={series} type="bar" height={300} />
  );
}

export { BalanceChart };
